import React, {useState} from 'react';
import {Sponsorship, SponsorshipData} from "types/Sponsorship";
import {Form, Formik, FormikHelpers, FormikProps} from "formik";
import Field from "components/Form/Field";
import FormControls from "components/FormControls";
import FieldHeader from "components/Form/FieldHeader";
import MoneyInput from "components/Form/MoneyInput";
import {formatDateTimeForInput} from "services/date";
import {DateTime} from "luxon";
import {useQuery} from "react-query";
import ngage from 'services/ngage';
import Selector from "components/Form/Selector";
import IconName from "types/IconName";
import {useOperator} from "context/OperatorContext";
import {Option, SingleCombobox} from "components/Form/Form/Control";
import useSponsorableOptions, {sponsorableAsOption} from "../../../hooks/useSponsorableOptions";
import useSponsorOptions, {sponsorAsOption} from "../../../hooks/useSponsorOptions";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";

// Types ----

type SponsorshipFormProps = {
  sponsorship?: Sponsorship
  onSubmit: (values: SponsorshipData, helpers: FormikHelpers<SponsorshipData>) => any
}

// Component ----

function SponsorshipForm({onSubmit, sponsorship}: SponsorshipFormProps) {
  const {termFor} = useOperator();

  // Keep track of the selected sponsors and sponsorable entities
  const [selectedSponsor, setSelectedSponsor] = useState<Option | null>(sponsorship?.sponsor ? sponsorAsOption(sponsorship.sponsor) : null);
  const [selectedSponsorable, setSelectedSponsorable] = useState<Option | null>(sponsorship ? sponsorableAsOption(sponsorship.sponsorable, sponsorship.sponsorable_type) : null)

  // Get available sponsorship tiers
  const {data: tiers, isLoading: tiersLoading} = useQuery(
    ['sponsorship-tiers', {page: 1}],
    () => ngage.sponsorshipTiers.all(1)
  )

  // Get available sponsors
  const [sponsorsSearchTerm, setSponsorsSearchTerm] = useState<string>()
  const {data: sponsors, isLoading: sponsorsLoading} = useSponsorOptions(sponsorsSearchTerm)

  // Get available sponsorable entities
  const [sponsorablesSearchTerm, setSponsorablesSearchTerm] = useState<string>()
  const {data: sponsorables, isLoading: sponsorablesLoading} = useSponsorableOptions(sponsorablesSearchTerm)

  function handleSelectSponsor(sponsor: Option | null, formik: FormikProps<SponsorshipData>) {
    if (sponsor) {
      void formik.setFieldValue('sponsor.type', sponsor.type)
      void formik.setFieldValue('sponsor.id', sponsor.id)
      setSelectedSponsor(sponsor)
    }
  }

  function handleSelectSponsorable(sponsorable: Option | null, formik: FormikProps<SponsorshipData>) {
    if (sponsorable) {
      void formik.setFieldValue('sponsorable.type', sponsorable.type)
      void formik.setFieldValue('sponsorable.id', sponsorable.id)
      setSelectedSponsorable(sponsorable)
    }
  }

  const validationSchema = Yup.object().shape({
    tier_id: Yup.number().notOneOf([0], 'This field is required').required("This field is required"),
    starts_at: Yup.date().required("This field is required"),
    sponsor: Yup.object().shape({
      id: Yup.number().required("This field is required"),
    }),
    sponsorable: Yup.object().shape({
      id: Yup.number().required("This field is required"),
    }),
    name: Yup.string().max(250, "Maximum character limit exceeded"),
  })

  const initialValues: SponsorshipData = {
    name: sponsorship?.name || '',
    tier_id: sponsorship?.tier.id || 0,
    paid_amount: sponsorship?.paid_amount || 0,
    starts_at: formatDateTimeForInput(sponsorship?.starts_at || DateTime.now(), true) as string,
    sponsor: {
      type: sponsorship?.sponsor_type,
      id: sponsorship?.sponsor?.id,
    },
    sponsorable: {
      type: sponsorship?.sponsorable_type,
      id: sponsorship?.sponsorable.id,
    },
  }

  return (
    <Formik<SponsorshipData> initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {formik => {
        const sponsorErrors = formik.errors.sponsor as { id?: string; type?: string } | undefined;
        const sponsorableErrors = formik.errors.sponsorable as { id?: string; type?: string } | undefined;

        return (
        <Form className="space-y-8 divide-y divide-gray-200">
          <ErrorNotification formik={formik} />

          <div className="shadow sm:rounded-md">
            <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
              <div className="space-y-8 divide-y divide-gray-200">
                <div>
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Key Information
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Basic details about the {termFor('sponsorship')}
                    </p>
                  </div>
                  <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                    {/* Tier */}
                    <div className="sm:col-span-3">
                      <Selector
                        label="Tier *"
                        isLoading={tiersLoading}
                        items={tiers?.data.map(tier => ({
                          id: tier.id,
                          name: tier.name,
                          description: `${tier.duration.toHuman()} for ${tier.price_formatted}`,
                          icon: IconName.CHART_BAR
                        })) || []}
                        selectedId={initialValues.tier_id}
                        onChange={item => formik.setFieldValue('tier_id', item.id)}
                      />

                      {(!!formik.errors.tier_id && !!formik.touched.tier_id) &&
                        <FieldHelp isError>
                          {formik.errors.tier_id}
                        </FieldHelp>
                      }
                    </div>

                    {/* Starts At */}
                    <div className="sm:col-span-3 md:col-span-2">
                      <FieldHeader label="Starts At *"/>
                      <div className="mt-1">
                        <Field
                          type="date"
                          name="starts_at"
                          id="starts_at"
                        />
                      </div>
                    </div>

                    {/* Sponsor */}
                    <div className="sm:col-span-3 sm:col-start-1">
                      <SingleCombobox
                        label={`${termFor('Sponsor')} *`}
                        hint="Type to search"
                        options={sponsors ?? []}
                        selected={selectedSponsor}
                        onSearch={setSponsorsSearchTerm}
                        onChange={option => handleSelectSponsor(option, formik)}
                        isLoading={sponsorsLoading}
                      />

                      {!!sponsorErrors && formik.touched.sponsor &&
                        <FieldHelp isError={!!sponsorErrors}>
                          {sponsorErrors.id}
                        </FieldHelp>
                      }
                    </div>

                    {/* Sponsorable Entity */}
                    <div className="sm:col-span-3">
                      <SingleCombobox
                        label="Entity *"
                        hint="Type to search"
                        options={sponsorables ?? []}
                        selected={selectedSponsorable}
                        onSearch={setSponsorablesSearchTerm}
                        onChange={option => handleSelectSponsorable(option, formik)}
                        isLoading={sponsorablesLoading}
                      />

                      {!!sponsorableErrors && formik.touched.sponsorable &&
                        <FieldHelp isError={!!sponsorableErrors}>
                          {sponsorableErrors.id}
                        </FieldHelp>
                      }
                    </div>

                    {/* Name */}
                    <div className="sm:col-span-3 sm:col-start-1">
                      <FieldHeader label="Display Name" hint="Optional. 250 max chars."/>
                      <div className="mt-1">
                        <Field
                          type="text"
                          name="name"
                          id="name"
                          autoComplete="off"
                          maxLength="250"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-8">
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Payment
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Record payment towards this {termFor('sponsorship')}
                    </p>
                  </div>
                  <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                    {/* Paid */}
                    <div className="sm:col-span-2">
                      <FieldHeader label="Amount Paid *"/>
                      <div className="mt-1">
                        <MoneyInput
                          currency={MoneyInput.Currency.GBP}
                          value={initialValues.paid_amount}
                          onChange={amount => formik.setFieldValue('paid_amount', amount)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <FormControls formik={formik}/>
          </div>
        </Form>
      )}}
    </Formik>
  );
}

export default SponsorshipForm;